import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  approveProfileData,
  getProfileDataRequirements,
  getProfileDataVerification,
  getVerificationState,
  ProfileDataVerification,
} from "api/profileDataVerification";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import infoIcon from "assets/icons/info.svg";
import { useAppDispatch } from "store";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { handleApiError } from "helpers/handleApiError";
import { useTranslation } from "react-i18next";

const formatFieldLabel = (fieldKey: string) =>
  fieldKey
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (character) => character.toUpperCase());

const renderValue = (value: unknown) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

interface Props {
  userId: number;
  onActionChange: (action: ProfileDataAction | null) => void;
}

export interface ProfileDataAction {
  status?: string;
  configured: boolean;
  canApprove: boolean;
  isLoading: boolean;
  isApproving: boolean;
  approve: () => void;
}

const ProfileDataTab: React.FC<Props> = ({ userId, onActionChange }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [requirements, setRequirements] = useState<string[]>([]);
  const [profileData, setProfileData] =
    useState<ProfileDataVerification | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isApproving, setIsApproving] = useState(false);

  const loadData = useCallback(async () => {
    const [requirementsResponse, profileResponse] = await Promise.all([
      getProfileDataRequirements(),
      getProfileDataVerification(userId),
      getVerificationState(userId),
    ]);

    setRequirements(requirementsResponse.data.fields ?? []);
    setProfileData(profileResponse.data);
  }, [userId]);

  useEffect(() => {
    let isActive = true;

    setIsLoading(true);
    loadData()
      .catch(() => undefined)
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [loadData]);

  const fieldsByKey = useMemo(
    () => new Map(profileData?.fields.map((field) => [field.fieldKey, field])),
    [profileData],
  );

  const handleApprove = useCallback(async () => {
    setIsApproving(true);
    try {
      await approveProfileData(userId);
      await loadData();
      dispatch(
        showNotification({
          title: t("success"),
          message: t("dataHasBeenChanged"),
          type: NotificationTypes.success,
        }),
      );
    } catch (error) {
      handleApiError({ error, t, dispatch });
    } finally {
      setIsApproving(false);
    }
  }, [dispatch, loadData, t, userId]);

  useEffect(() => {
    onActionChange({
      status: profileData?.status,
      configured: profileData?.configured ?? false,
      canApprove: profileData?.canApprove ?? false,
      isLoading,
      isApproving,
      approve: handleApprove,
    });

    return () => onActionChange(null);
  }, [handleApprove, isApproving, isLoading, onActionChange, profileData]);

  if (isLoading) return <ModalTabSkeleton />;

  if (requirements.length === 0) {
    return (
      <div className="profile-data-empty">
        No Profile Data requirements configured
      </div>
    );
  }

  return (
    <div className="profile-data-content">
      <div className="profile-data-summary">
        <div>
          <span className="profile-data-title">Profile Data</span>
          {profileData?.status && (
            <span className={`badge badge--${profileData.status}`}>
              {profileData.status}
            </span>
          )}
        </div>
      </div>

      <div className="profile-data-subtitle">Verification fields</div>

      <div className="verification-history profile-data-table">
        <div className="inner-table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Field</th>
                <th>Value</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {requirements.map((fieldKey) => {
                const field = fieldsByKey.get(fieldKey);

                return (
                  <tr key={fieldKey}>
                    <td>{formatFieldLabel(fieldKey)}</td>
                    <td>{renderValue(field?.value)}</td>
                    <td>
                      {field?.status ? (
                        <span className={`badge badge--${field.status}`}>
                          {field.status}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="profile-data-hint">
        <img src={infoIcon} alt="" />
        <span>All configured fields must be reviewed before approval.</span>
      </div>

    </div>
  );
};

export default ProfileDataTab;
